import { ViewBooking } from "collins-js-sdk";
import { ChevronDown, Clock } from "lucide-react";
import React, { useState } from "react";
import { VenueGroupConfig } from "../../../../../../../types/VenueGroup";
import { BookingStage, VenueConfig } from "../../../../../../../types/venues";
import {
  getBookingStageFromKey,
  getExcludedStages,
  getBookingStages as getVenueBookingStages,
  isSameCollinsDay,
  walkingBookings
} from "../../../../../../../utils/booking";
import { WebViewPopover } from "../../../../SubHeader/WebViewPopover";
import { isTouchDevice } from "../utils/bookingCardUtils";

interface DiaryBookingCardStatusIndicatorProps {
  booking: ViewBooking;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
  isUpdating: boolean;
  optimisticStage?: string | null;
  /** When true, the indicator becomes interactive with a stage selection dropdown. */
  isInteractive?: boolean;
  /** When true, applies a larger width and full height for waitlisted bookings. */
  isWaitlisted?: boolean;
  /** Called when a stage is selected from the dropdown. */
  onStageChange?: (stageId: string) => void;
  /** Controlled popover open state from parent (ensures only one popover open at a time). */
  isPopoverOpen?: boolean;
  /** Callback to update popover open state in parent. */
  onPopoverOpenChange?: (open: boolean) => void;
}

interface StageDropdownContentProps {
  booking: ViewBooking;
  stages: BookingStage[];
  displayStage: BookingStage;
  stageColour: string;
  onStageChange: (stageId: string) => void;
  onClose: () => void;
}

const EXCLUDED_DROPDOWN_STAGES = new Set(["left", "no-show", "rejected"]);

function resolveDisplayStage(
  booking: ViewBooking,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig,
  isUpdating: boolean,
  optimisticStage: string | null | undefined
): { displayStage: BookingStage; stageColour: string; stages: BookingStage[] } {
  const effectiveStage =
    isUpdating || optimisticStage !== null
      ? optimisticStage
      : booking.currentStage;

  const stages =
    venueConfig && venueGroupConfig
      ? getVenueBookingStages({ venueConfig, venueGroupConfig })
      : [];

  const stageFromKey = getBookingStageFromKey(
    effectiveStage ?? "",
    venueConfig,
    venueGroupConfig
  );

  const optimisticStageMatch = optimisticStage
    ? stages.find((stage) => stage.id === optimisticStage)
    : null;

  const fallbackStage: BookingStage = {
    id: effectiveStage ?? "",
    label: effectiveStage ? "Unknown Stage" : "Unchecked",
    colour: "6b7280",
    walk_in_default: false
  };

  const currentStage: BookingStage =
    stageFromKey ?? optimisticStageMatch ?? fallbackStage;

  const walkInStage = walkingBookings(
    effectiveStage ?? "",
    venueConfig,
    venueGroupConfig
  );

  const isToday = isSameCollinsDay(new Date(), new Date(booking.date ?? ""));
  const currentStageWalkIn: BookingStage = isToday
    ? (walkInStage ?? currentStage)
    : currentStage;

  const displayStage = booking.walkIn ? currentStageWalkIn : currentStage;
  const stageColour = displayStage?.colour
    ? `#${displayStage.colour}`
    : "#6b7280";

  return { displayStage, stageColour, stages };
}

const StageDropdownContent: React.FC<StageDropdownContentProps> = ({
  booking,
  stages,
  displayStage,
  stageColour,
  onStageChange,
  onClose
}) => {
  const excludedStages = getExcludedStages();
  const availableStages = stages.filter(
    (stage: BookingStage) =>
      !excludedStages.includes(stage.id.toLowerCase()) &&
      stage.id !== booking.currentStage
  );

  return (
    <div className="p-2 space-y-1" role="menu">
      {/* Current stage indicator at top of dropdown */}
      {booking.currentStage && (
        <div className="px-3 py-2 text-sm rounded-md flex items-center gap-3 bg-accent/50">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
            style={{ backgroundColor: stageColour }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
            />
            <span className="relative z-10">
              {displayStage?.label?.charAt(0).toUpperCase()}
            </span>
          </div>
          <span className="font-medium">{displayStage?.label}</span>
        </div>
      )}

      {booking.currentStage && <div className="border-b border-border my-1" />}

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left text-sm rounded-md flex items-center gap-3 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onStageChange("uncheck");
          onClose();
        }}
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
          style={{ backgroundColor: "#6b7280" }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
          />
          <Clock className="w-3 h-3 relative z-10" />
        </div>
        <span>Uncheck</span>
      </button>

      {availableStages.map((stage: BookingStage) => (
        <button
          key={stage.id}
          role="menuitem"
          className="w-full px-3 py-2 text-left text-sm rounded-md flex items-center gap-3 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
          onClick={(e) => {
            e.stopPropagation();
            onStageChange(stage.id);
            onClose();
          }}
        >
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-black text-xs font-bold relative"
            style={{
              backgroundColor: stage.colour ? `#${stage.colour}` : "#666666"
            }}
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
            />
            <span className="relative z-10">
              {stage.label.charAt(0).toUpperCase()}
            </span>
          </div>
          <span>{stage.label}</span>
        </button>
      ))}
    </div>
  );
};

export const DiaryBookingCardStatusIndicator =
  React.memo<DiaryBookingCardStatusIndicatorProps>(
    ({
      booking,
      venueConfig,
      venueGroupConfig,
      isUpdating,
      optimisticStage = null,
      isInteractive = false,
      isWaitlisted = false,
      onStageChange,
      isPopoverOpen: controlledPopoverOpen,
      onPopoverOpenChange
    }) => {
      const [isHovering, setIsHovering] = useState(false);
      const [internalPopoverOpen, setInternalPopoverOpen] = useState(false);

      // Use controlled state from parent when provided, otherwise fall back to internal state
      const isPopoverOpen = controlledPopoverOpen ?? internalPopoverOpen;
      const setIsPopoverOpen = onPopoverOpenChange ?? setInternalPopoverOpen;

      const { displayStage, stageColour, stages } = resolveDisplayStage(
        booking,
        venueConfig,
        venueGroupConfig,
        isUpdating,
        optimisticStage
      );

      const stageLabel = booking.currentStage
        ? displayStage?.label?.charAt(0).toUpperCase()
        : null;

      const shouldShowDropdown =
        isInteractive &&
        !EXCLUDED_DROPDOWN_STAGES.has(booking.currentStage ?? "");

      const indicatorWidth = isInteractive ? 26 : undefined;

      const showChevronHint = isInteractive && isTouchDevice();
      const baseChevronOpacity = showChevronHint ? 0.4 : 0;
      const chevronOpacity = isHovering ? 1 : baseChevronOpacity;

      const indicatorContent = (
        <>
          <div
            className={`DiarySidebarListItem__state DiarySidebarListItem__state--${
              displayStage?.label ?? "awaiting"
            }${isWaitlisted ? " DiarySidebarListItem__state--waitlist" : ""}`}
            style={
              indicatorWidth
                ? {
                    flex: `0 0 ${indicatorWidth}px`
                  }
                : undefined
            }
          >
            <span className="BookingStage_Label">
              {stageLabel ?? <Clock size={16} style={{ color: "black" }} />}
            </span>
            <div
              className="BookingStage_Colour"
              style={{ backgroundColor: stageColour }}
            />
          </div>

          {/* Hover/touch overlay with chevron — matching collins-mfe pattern */}
          {isInteractive && !isUpdating && (
            <div
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-200 pointer-events-none z-20"
              style={{
                backgroundColor: stageColour,
                borderRadius: 4,
                opacity: chevronOpacity
              }}
            >
              <ChevronDown
                className="text-white transition-opacity duration-200 pointer-events-none"
                size={18}
                style={{ opacity: chevronOpacity }}
                aria-hidden="true"
              />
            </div>
          )}

          {/* Loading overlay */}
          {isUpdating && (
            <div
              className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
              style={{
                backgroundColor: stageColour,
                borderRadius: 4
              }}
            >
              <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin pointer-events-none" />
            </div>
          )}
        </>
      );

      const stageIndicator = isInteractive ? (
        <button
          type="button"
          className={`relative appearance-none bg-transparent border-0 p-0 m-0 cursor-pointer text-inherit font-inherit${isWaitlisted ? " h-full" : ""}`}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          aria-label={`Change stage: ${displayStage?.label ?? "Unchecked"}`}
        >
          {indicatorContent}
        </button>
      ) : (
        <div className="relative">{indicatorContent}</div>
      );

      if (shouldShowDropdown && onStageChange) {
        return (
          <WebViewPopover
            side="bottom"
            sideOffset={8}
            open={isPopoverOpen}
            onOpenChange={setIsPopoverOpen}
            content={
              <StageDropdownContent
                booking={booking}
                stages={stages}
                displayStage={displayStage}
                stageColour={stageColour}
                onStageChange={onStageChange}
                onClose={() => setIsPopoverOpen(false)}
              />
            }
          >
            {stageIndicator}
          </WebViewPopover>
        );
      }

      return stageIndicator;
    }
  );

DiaryBookingCardStatusIndicator.displayName = "DiaryBookingCardStatusIndicator";
